import type * as ClassTypes from './types/class';
import {RESIZE_EVENTS, WIDGET_API_URL, WIDGET_URL} from './constants';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import type {IValidation} from '@/components/form/validation/types';
import Observer from './observer';
import {Utils} from './utils';
import Validation from '@/components/form/validation';

const observer: IObserver = new Observer();
const validation: IValidation = new Validation();

export const initTouchScroll = (element: HTMLElement): void => {
    import(/* webpackChunkName: "touch-scroll" */ '@/components/touch-scroll')
        .then(({default: TouchScroll}: IDynamicImport) => {
            const touchScroll = new TouchScroll(element);

            touchScroll.init();
        });
};

export const initPlanoplanWidget = (uid: string, lang: string = 'en', el: HTMLElement | string = 'planoplan'): void => {
    const primaryColor =
        getComputedStyle(document.documentElement).getPropertyValue('--color-brand-base') || null;

    const initWidget = (): void => {
        if (!window.Planoplan || typeof window.Planoplan.init !== 'function') {
            return;
        }

        window.Planoplan.init({
            uid,
            lang,
            el             : `#${el}`,
            width          : '100%',
            height         : '100%',
            textColor      : '#000000',
            backgroundColor: '#ffffff',
            apiLink        : WIDGET_API_URL,
            ...primaryColor && {primaryColor}
        });
    };

    const waitForPlanoplan = (): void => {
        let attempts = 0;
        // ~5 секунд
        const maxAttempts = 100;

        const check = setInterval(() => {
            attempts++;
            if (window.Planoplan && typeof window.Planoplan.init === 'function') {
                clearInterval(check);
                initWidget();
            } else if (attempts > maxAttempts) {
                clearInterval(check);
                console.error('Planoplan widget failed to load.');
            }
        }, 50);
    };

    if (window.Planoplan && typeof window.Planoplan.init === 'function') {
        initWidget();
    } else {
        const planoplanScript = document.createElement('script');

        planoplanScript.src = `${WIDGET_URL}?${Date.now()}`;
        planoplanScript.async = true;

        planoplanScript.onload = () => {
            waitForPlanoplan();
        };

        document.body.appendChild(planoplanScript);
    }
};

// Инициализация форм
export const initForm = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "form" */ '@/components/form')
        .then(({default: Form}: IDynamicImport) => {
            new Form().init({
                target        : wrapper,
                successMessage: true
            });
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackFormElement = popup.content.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[]): void => {
    const options = {
        removeDelay         : 600,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            const t: ClassTypes.IPopupFacade = new PopupFacade(options);

            t.init();
        });
};

export const initInfoPoint = (point: HTMLElement): void => {
    import(/* webpackChunkName: "infoPoint" */ '@/components/info-point')
        .then(({default: InfoPoint}: IDynamicImport) => {
            new InfoPoint(point).init();
        });
};

// Установка размеров видео первого экрана
export const initFullVideo = (fullIframe: HTMLElement): void => {
    const wrapper = document.querySelector('.j-first-screen__video') as HTMLElement;
    const ratio = 0.5625;

    if (!wrapper) {
        return;
    }

    const setVideoSize = (): void => {
        const width = Utils.getWindowWidth();
        const height = Utils.isMobile() ? wrapper.offsetHeight : Utils.getWindowHeight();
        const dimension = height / width;

        if (dimension > ratio) {
            fullIframe.style.width = `${height / ratio}px`;
            fullIframe.style.height = `${height + Math.abs(width - (height / ratio))}px`;
            fullIframe.style.top = `${0.5 * (width - (height / ratio))}px`;
            fullIframe.style.left = `${0.5 * (width - (height / ratio))}px`;
        } else {
            fullIframe.style.width = `${width}px`;
            fullIframe.style.height = `${(width * ratio) + (height * ratio)}px`;
            fullIframe.style.top = `${-Math.abs(0.5 * (height * ratio))}px`;
            fullIframe.style.left = '0';
        }
    };

    RESIZE_EVENTS.forEach((event: string) => {
        window.addEventListener(event, setVideoSize);
    });

    setVideoSize();
};

export const initKinescopeVideoPlayer = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "kinescope" */ '@/components/kinescope')
        .then(({default: Kinescope}: IDynamicImport) => {
            const kinescope = new Kinescope(wrapper);

            kinescope.init();
        });
};

const replaceSource = (element: Element): void => {
    const parentElement = element.parentElement;

    if (!parentElement) {
        return;
    }

    if (parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
        element.addEventListener('load', () => {
            parentElement.style.backgroundImage = '';
        });
    }

    const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
    const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

    if (element.getAttribute(sourceElement) !== null) {
        element.setAttribute(targetAttribute, element.getAttribute(sourceElement) || '');
        element.removeAttribute(sourceElement);
    }
};

const replaceVideoSource = (element: Element): void => {
    const poster = element.getAttribute('data-poster');
    const sourceElement = element.querySelector('source');

    if (poster) {
        (element as HTMLVideoElement).setAttribute('poster', poster);
        (element as HTMLVideoElement).removeAttribute('data-poster');
    }

    if (sourceElement?.getAttribute('data-src')) {
        sourceElement.setAttribute('src', sourceElement.getAttribute('data-src') || '');
        sourceElement.removeAttribute('data-src');
    }

    (element as HTMLVideoElement).load();

    (element as HTMLVideoElement).play().catch(() => {
        console.error('Автоплей заблокирован (iOS/Safari)');

        (element as HTMLVideoElement).style.display = 'none';

        // Показываем постер
        const posterElement = (element as HTMLVideoElement).getAttribute('poster');

        if (posterElement) {
            const img = document.createElement('img');

            img.src = posterElement;
            img.className = 'video-fallback';
            (element as HTMLVideoElement).insertAdjacentElement('afterend', img);
        }
    });
};

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements: Element[]): void => {
    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const lazyImageObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;
                    const isSlider = lazyEl.closest('.swiper-slide');

                    if (isSlider) {
                        const allSlidesImages =
                            [...lazyEl.closest('.swiper-wrapper')!
                                .querySelectorAll<HTMLImageElement>('img[loading="lazy"]')];

                        allSlidesImages.forEach((image: HTMLImageElement) => {
                            replaceSource(image);
                            lazyImageObserver.unobserve(image);
                        });
                    } else {
                        if (lazyEl.nodeName === 'VIDEO') {
                            replaceVideoSource(lazyEl);
                        } else {
                            replaceSource(lazyEl);
                        }

                        lazyImageObserver.unobserve(lazyEl);
                    }
                }
            });
        });

        lazyElements.forEach((lazyImage: Element) => {
            const offset = lazyImage.getAttribute('data-offset') ?
                window.innerHeight * 2 || document.documentElement.clientHeight * 2 :
                window.innerHeight || document.documentElement.clientHeight;

            if (Utils.isInViewport(lazyImage as HTMLElement, offset)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl: Element) => {
            replaceSource(lazyEl);
        });
    }
};
